import {AnalyticsBrowser} from '@segment/analytics-next';
import * as Sentry from '@sentry/react';
import {devLog} from 'utils/devLog';
import {growthBook} from 'utils/growthbook';
import packageJson from '../../package.json';
import Environment from './Environment';
import Identity from './Identity';

let loadStartTime = 0;
function setLoadStartTime() {
  loadStartTime = performance.now();
}
function timeSinceLoad() {
  return performance.now() - loadStartTime;
}
const segment = AnalyticsBrowser.load({writeKey: 'wvZrXKtcsn0RWcqV3ixaEZED0xb5K0et'});
console.log(packageJson.version);

const metaPayload = () => {
  return {
    version: packageJson.version,
    environment: Environment.getVar('REACT_APP_ENV') ?? 'test',
    host: window.location.host,
    url: window.location.href,
    urlParams: window.location.search,
    userAgent: window.navigator.userAgent,
    timeSinceLoadMs: timeSinceLoad(),
  };
};

function track(eventName: string, payload: object = {}) {
  // Because some cases we've seen the ID disappear from localStorage, we'll aggressively ensure it exists.
  const anonId = Identity.ensureAnonymousID();
  const completePayload = {...metaPayload(), web_funnel_anon_id: anonId, ...payload};
  const completeEventName = `[Web] ${eventName}`;
  const sent = segment.track(completeEventName, completePayload);

  devLog(`Sent to Amplitude: "${eventName}". Payload: ${JSON.stringify(completePayload, null, '\t')}`);
  // Callers that navigate away right after tracking can await this so the
  // event isn't dropped with the page.
  return sent;
}

type IdentifyPayload = Record<string, string>;
type AnonymousIdentify = (payload: IdentifyPayload) => void;
type IdentifiedIdentify = (id: string | number, payload?: IdentifyPayload) => void;
const identify: IdentifiedIdentify & AnonymousIdentify = (
  idOrPayload: string | number | IdentifyPayload,
  payload?: IdentifyPayload,
) => {
  const anonId = Identity.ensureAnonymousID();
  const hasPersonId = typeof idOrPayload === 'string' || typeof idOrPayload === 'number';
  //id if known id
  if (hasPersonId) {
    const personId = String(idOrPayload);
    const attr = growthBook.getAttributes();

    growthBook.setAttributes({
      ...attr,
      personId,
      email: payload?.email ?? attr.email,
    });

    segment.identify(personId, {...metaPayload(), ...payload, web_funnel_anon_id: anonId});
    // Update Sentry user context with web_funnel_anon_id and person_id as separate fields
    Sentry.setUser({
      web_funnel_anon_id: anonId,
      person_id: personId,
      email: payload?.email,
    });
    devLog(`Sent to Amplitude Identify: "${personId}". Payload: ${JSON.stringify(payload, null, '\t')}`);
  } else {
    //payload if anonymous id
    segment.identify({...metaPayload(), ...idOrPayload, web_funnel_anon_id: anonId});
    // For anonymous users, only set the web_funnel_anon_id
    Sentry.setUser({
      web_funnel_anon_id: anonId,
    });
    devLog(`Sent to Amplitude Identify: "${anonId}". Payload: ${JSON.stringify(idOrPayload, null, '\t')}`);
  }
};

const waitFor = (ms: number) => new Promise(resolve => setTimeout(resolve, ms));

const analyticsIsReady = () => !Array.isArray(segment);

/**
 * In some cases, we need to ensure that Segment is initialized before sending events
 * (redirecting back to the Checkout Success Page from Stripe Checkout is one example)
 * By default: waits 200 ms and retries 5 times.
 */
const onReady = async (analytics: () => void, retries = 5, waitInMs = 200): Promise<void> => {
  for (let i = 0; i < retries; i++) {
    if (analyticsIsReady()) {
      analytics();
      return;
    }
    await waitFor(waitInMs);
  }
  // Sentry.captureException(new Error("Analytics didn't initialize within window"));
};

/**
 * Calls the tracking event only after Analytics is ready
 * @param eventName
 * @param payload
 */
const trackOnReady = (eventName: string, payload: object = {}) => {
  onReady(() => {
    track(eventName, payload);
  });
};
const Analytics = {track, identify, onReady, segment, setLoadStartTime, timeSinceLoad, trackOnReady};
export default Analytics;
